import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import {
  validateCoffeeTextFields,
  normalizeTextField,
  normalizeCoffeeConstrainedFields,
} from '@/lib/coffeeValidation';
import PlaceAutocomplete from '@/components/places/PlaceAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, MapPin, CheckCircle, Upload, X, Loader2 } from 'lucide-react';
import StarRating from '@/components/coffee/StarRating';
import ExistingCoffeeDialog from '@/components/coffee/ExistingCoffeeDialog';
import ThankYouModal from '@/components/coffee/ThankYouModal';
import CafeCoffeeSelect from '@/components/coffee/CafeCoffeeSelect';
import WriteReviewModal from '@/components/coffee/WriteReviewModal';
import { scrollFocusedFieldIntoView } from '@/lib/scrollIntoViewOnFocus';

const STEPS = ['Find Cafe', 'Select Coffee', 'Coffee Details', 'Write Review'];

export default function AddCoffee() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [coffeeData, setCoffeeData] = useState({
    name: '',
    bean_origin: '',
    varietal: '',
    processing_method: '',
    roaster_name: '',
    brew_method: '',
    availability: 'Regular',
    photo_url: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [review, setReview] = useState({ rating: 0, recipes: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createdCoffeeId, setCreatedCoffeeId] = useState(null);
  const [error, setError] = useState('');
  const [duplicateResult, setDuplicateResult] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [reviewingCoffee, setReviewingCoffee] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleNext = async () => {
    if (step === 0 && !selectedPlace) { setError('Please select a cafe first.'); return; }
    if (step === 2 && !coffeeData.name) { setError('Coffee name is required.'); return; }

    if (step === 2) {
      const { isValid, errors } = validateCoffeeTextFields(coffeeData);
      if (!isValid) {
        setError(Object.values(errors)[0]);
        return;
      }
    }
    setError('');

    // Coffee Details → Review: check for duplicate before proceeding
    if (step === 2) {
      setCheckingDuplicate(true);
      try {
        const res = await api.functions.invoke('checkCoffeeDuplicate', {
          place_id: selectedPlace.place_id,
          name: normalizeTextField(coffeeData.name),
        });
        if (res.data?.exists) {
          setDuplicateResult(res.data);
          return;
        }
      } catch (err) {
        console.error('Duplicate check failed', err);
        setError(err?.message || 'Could not check for an existing coffee. Please try again.');
        return;
      } finally {
        setCheckingDuplicate(false);
      }
    }

    setStep((s) => s + 1);
  };

  const handleWriteReviewExisting = async (coffee) => {
    try {
      const user = currentUser || (await api.auth.me());
      if (!currentUser) setCurrentUser(user);
      setReviewingCoffee(coffee);
    } catch (err) {
      console.error('Could not load the signed-in user', err);
      setError(err?.message || 'Could not start the review. Please sign in and try again.');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      let photo_url = coffeeData.photo_url;
      if (photoFile) {
        const { file_url } = await api.integrations.Core.UploadFile({ file: photoFile });
        photo_url = file_url;
      }

      // A retry after a failed review write must not add a second coffee, so
      // reuse the one this submit already created.
      let coffeeId = createdCoffeeId;
      if (!coffeeId) {
        const newCoffee = await api.entities.Coffee.create(normalizeCoffeeConstrainedFields({
          ...coffeeData,
          name: normalizeTextField(coffeeData.name),
          bean_origin: normalizeTextField(coffeeData.bean_origin),
          varietal: normalizeTextField(coffeeData.varietal),
          roaster_name: normalizeTextField(coffeeData.roaster_name),
          photo_url,
          place_id: selectedPlace.place_id,
          cafe_name: selectedPlace.name,
          cafe_area: selectedPlace.area,
          // Sent on every insert, null included, rather than omitted when
          // the address yields no city: the column used to default to
          // 'Bangkok', which filed such a cafe under the wrong city. The
          // default is gone, and the stored value is now always this one.
          city: selectedPlace.city || null,
        }));
        coffeeId = newCoffee.id;
        setCreatedCoffeeId(coffeeId);
      }

      if (review.rating > 0) {
        const user = await api.auth.me();
        await api.entities.Review.create({
          coffee_id: coffeeId,
          reviewer_id: user.id,
          reviewer_name: user.display_name || user.full_name || user.email,
          rating: review.rating,
          comment: review.comment,
        });
        // The coffee's average_rating and review_count are set by the reviews
        // trigger; writing them from here would be ignored by the column guard.
        setShowThankYou(true);
      } else {
        navigate(`/coffee/${coffeeId}`);
      }
    } catch (err) {
      // Without this the rejected promise left `submitting` stuck on true and
      // the button spinning on "Saving..." with nothing to explain it.
      console.error('Failed to save the coffee and its review', err);
      setError(err?.message || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (key, val) => setCoffeeData((d) => ({ ...d, [key]: val }));

  return (
    <>
    {duplicateResult && (
      <ExistingCoffeeDialog
        result={duplicateResult}
        onClose={() => setDuplicateResult(null)}
      />
    )}
    {showThankYou && (
      <ThankYouModal onClose={() => navigate(`/coffee/${createdCoffeeId}`)} />
    )}
    {reviewingCoffee && (
      <WriteReviewModal
        coffee={reviewingCoffee}
        user={currentUser}
        onClose={() => setReviewingCoffee(null)}
        onSubmitted={() => navigate(`/coffee/${reviewingCoffee.id}`)}
      />
    )}
    <div className="min-h-screen">

      {/* Header */}
      <div className="sticky top-14 z-30 bg-background/95 backdrop-blur border-b border-border px-4 pt-3 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)} className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
              <ArrowLeft size={16} />
            </button>
            <h1 className="font-heading font-semibold text-lg">Add a Review</h1>
          </div>
          {/* Step indicators */}
          <div className="flex items-start justify-center max-w-sm mx-auto w-full">
            {STEPS.map((label, i) => (
              <div key={i} className="flex items-start flex-1 min-w-0">
                <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                  <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold transition-colors ${
                    i < step ? 'bg-primary text-primary-foreground' :
                    i === step ? 'bg-accent text-accent-foreground' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {i < step ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <span className="text-[10px] text-muted-foreground text-center leading-tight">{label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-6 max-w-2xl mx-auto space-y-5">
        {/* STEP 0: Find Cafe */}
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-semibold text-base mb-1">Find the Cafe</h2>
              <p className="text-sm text-muted-foreground">Search for the cafe where this coffee is served.</p>
            </div>
            <PlaceAutocomplete
              onSelect={(place) => { setSelectedPlace(place); setError(''); }}
              placeholder="Search cafes in Bangkok..."
            />
            {selectedPlace && (
              <div className="flex items-start gap-3 bg-secondary/50 rounded-xl p-4 border border-primary/20">
                <MapPin size={18} className="text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-sm">{selectedPlace.name}</p>
                  {selectedPlace.area && <p className="text-xs text-muted-foreground mt-0.5">Area: {selectedPlace.area}</p>}
                  {selectedPlace.formatted_address && (
                    <p className="text-xs text-muted-foreground mt-0.5">{selectedPlace.formatted_address}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 1: Select existing coffee or add new */}
        {step === 1 && selectedPlace && (
          <CafeCoffeeSelect
            place={selectedPlace}
            onWriteReview={handleWriteReviewExisting}
            onAddNew={() => setStep(2)}
          />
        )}

        {/* STEP 2: Coffee Details */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-semibold text-base mb-1">Coffee Details</h2>
              <p className="text-xs text-muted-foreground">at <span className="font-medium text-foreground">{selectedPlace?.name}</span></p>
            </div>

            <FormField label="Coffee Name *">
              <Input value={coffeeData.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Geisha Washed, Signature Latte" className="rounded-xl" />
            </FormField>

            <FormField label="Recipes">
              <Textarea
                value={coffeeData.recipes || ''}
                onChange={(e) => setField('recipes', e.target.value)}
                placeholder="e.g. espresso shot, oat milk, vanilla syrup..."
                className="rounded-xl resize-none" rows={2}
              />
            </FormField>

            <FormField label="Brew Method">
              <Select value={coffeeData.brew_method} onValueChange={(v) => setField('brew_method', v)}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select brew method" /></SelectTrigger>
                <SelectContent>
                  {['Espresso', 'Hand drip', 'Aeropress', 'French press', 'Cold brew'].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Bean Origin">
                <Input value={coffeeData.bean_origin} onChange={(e) => setField('bean_origin', e.target.value)} placeholder="e.g. Ethiopia" className="rounded-xl" />
                {coffeeData.bean_origin && coffeeData.bean_origin.trim().length > 0 && coffeeData.bean_origin.trim().length < 2 && (
                  <p className="text-xs text-destructive mt-1">At least 2 characters required</p>
                )}
              </FormField>
              <FormField label="Varietal">
                <Input value={coffeeData.varietal} onChange={(e) => setField('varietal', e.target.value)} placeholder="e.g. Geisha" className="rounded-xl" />
                {coffeeData.varietal && coffeeData.varietal.trim().length > 0 && coffeeData.varietal.trim().length < 2 && (
                  <p className="text-xs text-destructive mt-1">At least 2 characters required</p>
                )}
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Processing Method">
                <Select value={coffeeData.processing_method} onValueChange={(v) => setField('processing_method', v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select processing" /></SelectTrigger>
                  <SelectContent>
                    {['Washed', 'Natural', 'Honey', 'Other'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Roaster Name">
                <Input value={coffeeData.roaster_name} onChange={(e) => setField('roaster_name', e.target.value)} placeholder="Optional" className="rounded-xl" />
                {coffeeData.roaster_name && coffeeData.roaster_name.trim().length > 0 && coffeeData.roaster_name.trim().length < 2 && (
                  <p className="text-xs text-destructive mt-1">At least 2 characters required</p>
                )}
              </FormField>
            </div>

            <FormField label="Availability">
              <Select value={coffeeData.availability} onValueChange={(v) => setField('availability', v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Limited time">Limited Time</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            {/* Coffee photo */}
            <FormField label="Coffee Photo (optional)">
              {photoPreview ? (
                <div className="relative">
                  <img src={photoPreview} alt="preview" className="max-h-56 max-w-[280px] object-contain rounded-xl" />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-xl p-5 cursor-pointer hover:border-primary/50 transition-colors">
                  <Upload size={20} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Tap to upload a photo</span>
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                </label>
              )}
            </FormField>
          </div>
        )}

        {/* STEP 3: First Review */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading font-semibold text-base mb-1">Write a Review <span className="text-muted-foreground font-normal text-sm">(optional)</span></h2>
              <p className="text-sm text-muted-foreground">Share your first impressions of <span className="font-medium text-foreground">{coffeeData.name}</span>.</p>
            </div>

            <div className="space-y-2">
              <Label>Rating</Label>
              <StarRating rating={review.rating} size={36} interactive onChange={(r) => setReview((v) => ({ ...v, rating: r }))} showLabel />
              {review.rating === 0 && (
                <span className="text-sm text-muted-foreground">Tap to rate</span>
              )}
            </div>

            <div className="space-y-2">
              <Label>Comment</Label>
              <Textarea
                value={review.comment}
                onChange={(e) => setReview((v) => ({ ...v, comment: e.target.value }))}
                onFocus={scrollFocusedFieldIntoView}
                placeholder="Share your thoughts..."
                className="rounded-xl resize-none" rows={3}
              />
            </div>
          </div>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        {/* Navigation */}
        <div className="pt-2">
          {step === 0 || step === 2 ? (
            <Button onClick={handleNext} disabled={checkingDuplicate} className="w-full rounded-xl">
              {checkingDuplicate ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {checkingDuplicate ? 'Checking...' : 'Continue'}
              {!checkingDuplicate && <ArrowRight size={16} className="ml-2" />}
            </Button>
          ) : step === 3 ? (
            <div className="space-y-2">
              <Button onClick={handleSubmit} disabled={submitting} className="w-full rounded-xl">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {submitting ? 'Saving...' : 'Add Review'}
              </Button>
              {review.rating === 0 && (
                <Button onClick={handleSubmit} disabled={submitting} variant="outline" className="w-full rounded-xl text-sm">
                  Skip review & add
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
    </>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5" onFocus={scrollFocusedFieldIntoView}>
      <Label className="text-sm font-medium">{label}</Label>
      {children}
    </div>
  );
}